import { styled } from '@mui/material/styles';
import {
  Popper,
  Grow,
  Paper as MuiPaper,
  ClickAwayListener,
  MenuList as MuiMenuList,
  MenuItem as MuiMenuItem,
  PopperPlacementType,
  Typography,
  Box,
} from '@mui/material';
import { SyntheticEvent, useRef, useState } from 'react';
import { MappedInsightType } from '../../queries/useInsightsQuery';
import ArrowRightSLineIcon from 'remixicon-react/ArrowRightSLineIcon';
import { Link } from 'react-router-dom';
import { InsightType } from '../../apis';
import { getEncodedDirectoryPath } from '../../helpers';

type MenuPopupProps = {
  anchorRefElement: HTMLDivElement | HTMLLIElement | null;
  directoryPath: string[];
  insightType: InsightType;
  menu: MappedInsightType[];
  open: boolean;
  placement?: PopperPlacementType;
  selectedItem?: string;
  handleClose: (event: Event | SyntheticEvent) => void;
  onMouseLeave?: (event: Event | SyntheticEvent) => void;
  getRedirectUrl?: (options: { directoryPath: string; insightType: InsightType }) => string;
};

function MenuPopup({
  anchorRefElement,
  directoryPath,
  insightType,
  menu,
  open,
  placement,
  selectedItem,
  handleClose,
  onMouseLeave,
  getRedirectUrl,
}: MenuPopupProps) {
  const [openChildMenuItem, setOpenChildMenuItem] = useState('');
  const listRef = useRef<HTMLOListElement>(null);
  const getDirectoryPath = (item: MappedInsightType) => [...directoryPath, item.title];

  return (
    <Popper
      open={open}
      anchorEl={anchorRefElement}
      role={undefined}
      placement={placement || 'bottom-start'}
      transition
      sx={{ zIndex: 9 }}
      onMouseLeave={onMouseLeave}
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
          }}
        >
          <Paper>
            <ClickAwayListener onClickAway={handleClose}>
              <MenuList autoFocusItem={open} id="menu" aria-labelledby="menu-list" ref={listRef}>
                {menu.map((item) => (
                  <Link
                    key={item.title}
                    to={
                      getRedirectUrl?.({
                        directoryPath: getEncodedDirectoryPath(getDirectoryPath(item)),
                        insightType,
                      }) || {
                        pathname: '',
                        search: `?directoryPath=${getEncodedDirectoryPath(getDirectoryPath(item))}&insightType=${insightType}`,
                      }
                    }
                  >
                    <MenuItem
                      onClick={handleClose}
                      onMouseEnter={() => setOpenChildMenuItem(item.title)}
                      sx={(theme) => ({
                        ...(openChildMenuItem === item.title && {
                          backgroundColor: theme.palette.custom.boxShadow,
                        }),
                      })}
                      selected={selectedItem === item.title}
                      data-hover-item={item.title}
                    >
                      <Typography whiteSpace="pre-wrap" mr={0.5}>
                        {item.displayTitle || item.title}
                      </Typography>
                      {item.children && item.children?.length > 0 && (
                        <Box component="span">
                          <ArrowRightSLineIcon size={22} />
                        </Box>
                      )}
                      {item.children &&
                        item.children.length > 0 &&
                        openChildMenuItem === item.title && (
                          <MenuPopup
                            open={!!openChildMenuItem}
                            anchorRefElement={
                              (listRef.current &&
                                listRef.current.querySelector(
                                  `[data-hover-item="${openChildMenuItem}"]`,
                                )) ||
                              anchorRefElement
                            }
                            handleClose={() => setOpenChildMenuItem('')}
                            onMouseLeave={() => setOpenChildMenuItem('')}
                            menu={item?.children || []}
                            placement="right-start"
                            directoryPath={getDirectoryPath(item)}
                            insightType={insightType}
                            getRedirectUrl={getRedirectUrl}
                          />
                        )}
                    </MenuItem>
                  </Link>
                ))}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
}

const Paper = styled(MuiPaper)(({ theme }) => ({
  boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
}));

const MenuList = styled(MuiMenuList)({
  width: 250,
  maxHeight: 400,
  overflow: 'auto',
});

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '&.Mui-selected': {
    backgroundColor: theme.palette.custom.mariner01,
  },
}));

export default MenuPopup;
